import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, DollarSign, AlertCircle } from 'lucide-react';

const ServiceChargeModal = ({ isOpen, onClose, onConfirm, serviceInfo, userType, user }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Format user type for display
  const formatUserType = (type) => {
    // Handle null/undefined
    if (!type) return 'User';
    
    const typeMap = {
      'a_la_carte': 'A la Carte',
      'free_user': 'Free User',
      'subscriber': 'Subscriber',
      'monthly': 'Monthly Subscriber',
      'annual': 'Annual Subscriber',
      'admin': 'Admin',
      'student': 'Student'
    };
    
    // Return mapped value or format the string safely
    if (typeMap[type]) return typeMap[type];
    
    // Safely handle string formatting
    if (typeof type === 'string') {
      return type.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    }
    
    return 'User';
  };

  // Support both userType prop and user object
  const effectiveUserType = userType || user?.role || user?.subscription_status || 'free_user';
  const displayUserType = formatUserType(effectiveUserType);

  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [isOpen]);

  if (!isOpen || !serviceInfo) return null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } catch (error) {
      console.error('Charge confirmation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    // Let the parent component decide what to do (MMG stays on page, others may navigate)
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 overflow-y-auto p-2 sm:p-4 sm:pt-20">
      <div className="bg-white rounded-lg p-4 sm:p-6 md:p-8 max-w-md w-full my-4">
        <div className="flex items-center mb-4 sm:mb-6">
          <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
          <h3 className="text-base sm:text-lg md:text-xl font-bold break-words">Service Charge Confirmation</h3>
        </div>
        
        <div className="mb-4 sm:mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mt-0.5 mr-2 sm:mr-3 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 mb-1 sm:mb-2 text-sm sm:text-base">Charge Notification</h4>
                <p className="text-blue-800 text-xs sm:text-sm break-words">
                  As an <strong>{displayUserType}</strong> user, your account will be charged <strong>${Number(serviceInfo.amount).toFixed(2)}</strong> to use <strong>{serviceInfo.name}</strong>.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
            <div className="text-xs sm:text-sm space-y-2">
              <div className="flex justify-between items-start gap-2">
                <span className="text-gray-600 flex-shrink-0">Service:</span>
                <span className="font-medium text-right break-words">{serviceInfo.name}</span>
              </div>
              
              {/* Display submission details if provided */}
              {serviceInfo.details && (
                <>
                  {serviceInfo.details.book_title && (
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-gray-600 flex-shrink-0">Book Title:</span>
                      <span className="font-medium text-right break-words max-w-[60%]" title={serviceInfo.details.book_title}>
                        {serviceInfo.details.book_title}
                      </span>
                    </div>
                  )}
                  {serviceInfo.details.book_type && (
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-gray-600 flex-shrink-0">Book Type:</span>
                      <span className="font-medium capitalize text-right">{serviceInfo.details.book_type}</span>
                    </div>
                  )}
                  {serviceInfo.details.manuscript_info && (
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-gray-600 flex-shrink-0">Manuscript:</span>
                      <span className="font-medium text-right break-words max-w-[60%]">{serviceInfo.details.manuscript_info}</span>
                    </div>
                  )}
                  {serviceInfo.details.isbn && (
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-gray-600 flex-shrink-0">ISBN:</span>
                      <span className="font-medium text-right break-words">{serviceInfo.details.isbn}</span>
                    </div>
                  )}
                </>
              )}
              
              <div className="flex justify-between items-start gap-2">
                <span className="text-gray-600 flex-shrink-0">Amount:</span>
                <span className="font-medium">${Number(serviceInfo.amount).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-start gap-2">
                <span className="text-gray-600 flex-shrink-0">User Type:</span>
                <span className="font-medium text-right break-words">{displayUserType}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4 sm:mb-6">
          <div className="flex items-center text-xs sm:text-sm text-gray-600">
            <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
            <span className="break-words">Payment will be processed using your saved payment method</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4 sm:gap-0">
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`w-full sm:flex-1 py-2.5 sm:py-3 px-4 rounded-lg font-semibold transition-colors text-sm sm:text-base ${
              loading
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? 'Processing...' : `Charge $${Number(serviceInfo.amount).toFixed(2)} & Continue`}
          </button>
          
          <button
            onClick={handleCancel}
            disabled={loading}
            className="w-full sm:flex-1 bg-gray-100 text-gray-700 py-2.5 sm:py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm sm:text-base"
          >
            Cancel
          </button>
        </div>

        <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
          <p className="text-[10px] sm:text-xs text-gray-500 text-center break-words">
            Charges are processed only upon successful completion of the service. Thank you for choosing Digital Ink AI.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ServiceChargeModal;
